import { getLang, hasKey, tr } from '@app/i18n'
import { localized, type RoutePlan, type Venue } from '@app/rvf'
import { categoryNames } from '../categories'
import { liveCategory, liveName } from '../live-card'

/**
 * Локализованная подпись категории. Приоритет: справочник, который ведёт админ
 * (U7 — он может переименовать «Склад» или завести свою категорию), затем
 * встроенный словарь, затем сама категория как есть из данных.
 */
export function categoryLabel(category: string): string {
  const own = categoryNames(category)
  if (own) {
    const lang = getLang()
    const name = own[lang] || own['en'] || own['et'] || own['ru']
    if (name) return name
  }
  const key = `cat.${category}`
  return hasKey(key) ? tr(key) : category
}

/**
 * Имя объекта на активном языке; безымянный узел — тип (Вход/Ворота).
 *
 * Живая карточка сильнее карты — правило и его причина в live-card.ts.
 */
export function featureName(venue: Venue, id: string): string {
  const live = liveName(venue, id)
  if (live) return live
  const unit = venue.units.find((u) => u.id === id)
  const poi = unit ? undefined : venue.pois.find((p) => p.id === id)
  const name = unit?.name ?? poi?.name
  const text = localized(name, getLang(), venue.venue.default_language)
  if (text) return text
  const node = venue.network.nodes.find((n) => n.id === id)
  if (node) {
    const own = localized(node.name, getLang(), venue.venue.default_language)
    const kindCat = node.kind === 'door' || node.kind === 'entrance' ? 'entrance' : node.kind
    return own || categoryLabel(kindCat)
  }
  return text
}

/** Категория юнита/POI/узла (для подписи и телефона в карточке). */
export function featureCategory(venue: Venue, id: string): string {
  // Категория из живой карточки сильнее — так обещано в кабинете арендатора
  // («влияет на значок и подпись объекта у посетителя»); «как на карте» в
  // кабинете = пустое значение, тогда работает категория из карты.
  const live = liveCategory(id)
  if (live) return live
  const unit = venue.units.find((u) => u.id === id)
  if (unit) return unit.category
  const poi = venue.pois.find((p) => p.id === id)
  if (poi) return poi.category
  return venue.network.nodes.find((n) => n.id === id)?.kind ?? ''
}

/**
 * Маршрут проходит более одного уровня. Гейт для UI подъёма: выбор
 * эскалатор/лифт/лестница уместен только когда путь реально меняет этаж —
 * на уличном маршруте многоэтажной площадки его быть не должно
 * (фидбек владельца 24.07).
 */
export function planCrossesLevels(venue: Venue, plan: RoutePlan): boolean {
  const levels = new Set<string>()
  for (const nodeId of plan.nodeIds) {
    const node = venue.network.nodes.find((n) => n.id === nodeId)
    if (node) levels.add(node.level)
  }
  return levels.size > 1
}

/** «1 → 3 этаж» для мультиэтажного маршрута (System B4); '' если один уровень. */
export function planLevelsLabel(venue: Venue, plan: RoutePlan): string {
  if (venue.levels.length < 2) return ''
  const levelOf = (nodeId: string | undefined): string | null => {
    const node = nodeId ? venue.network.nodes.find((n) => n.id === nodeId) : undefined
    return node ? node.level : null
  }
  const from = levelOf(plan.start.nodeId)
  const to = levelOf(plan.nodeIds[plan.nodeIds.length - 1])
  if (!from || !to || from === to) return ''
  const short = (id: string): string => id.replace(/^[LF]/i, '') || id
  return `${short(from)} → ${short(to)}`
}
